import { createUseStyles } from "react-jss";
import { MOBILE_WIDTH } from "../../consts";
import {
    alpha,
    backgroundless,
    borderColor,
    controlBackgroundColor,
    darken,
    foregroundColor,
    getButtonChoiceMargin,
    getSpacing,
} from "../../theme/Mixins";
import * as ChoiceTokens from "../../theme/choiceThemeTokens";
import { Theme } from "../../types/Theme";

type StyleProps = {
    theme: Theme;
    selected?: boolean;
    flatModernSurface?: boolean;
};

function isFlatModernSurface(theme: Theme, flatModernSurface?: boolean): boolean {
    return Boolean(flatModernSurface && ChoiceTokens.useModernChoiceStyles(theme));
}

function modernChoiceBorder(theme: Theme, color: string): string {
    return ChoiceTokens.resolveModernChoiceInsetBorder(theme, color);
}

function modernChoiceActiveBorder(theme: Theme, color: string): string {
    return ChoiceTokens.resolveModernActiveChoiceInsetBorder(theme, color);
}

function resolveModernChoiceFocusInsetBorder(theme: Theme, selected?: boolean): string {
    const borderColor = selected
        ? theme.PrimaryColor
        : ChoiceTokens.resolveChoiceBorderColorNormal(theme);
    return selected
        ? modernChoiceActiveBorder(theme, borderColor)
        : modernChoiceBorder(theme, borderColor);
}

/** Keyboard-only focus chrome (applied via JS); not shown on pointer click focus. */
const modernChoiceKeyboardFocusStyles = {
    boxShadow: ({ theme, selected, flatModernSurface }: StyleProps) => {
        if (isFlatModernSurface(theme, flatModernSurface)) {
            return "none";
        }
        return ChoiceTokens.useModernChoiceStyles(theme)
            ? resolveModernChoiceFocusInsetBorder(theme, selected)
            : undefined;
    },
    outline: ({ theme, selected }: StyleProps) =>
        ChoiceTokens.useModernChoiceStyles(theme)
            ? ChoiceTokens.resolveModernChoiceFocusOutline(theme, selected)
            : undefined,
    outlineOffset: ({ theme }: StyleProps) =>
        ChoiceTokens.useModernChoiceStyles(theme)
            ? ChoiceTokens.CHOICE_FOCUS_OUTLINE_OFFSET_PX
            : undefined,
};

export default createUseStyles({
    choice: {
        composes: "choice",
        background: ({
            theme,
            carrouselChoiceBackgound,
        }: {
            theme: Theme;
            carrouselChoiceBackgound?: string;
        }) => {
            if (carrouselChoiceBackgound) {
                return carrouselChoiceBackgound;
            }
            return alpha(theme.ControlBackground, theme.ChoiceListBackgroundAlpha);
        },
        boxShadow: ({ theme, flatModernSurface }: StyleProps) => {
            if (isFlatModernSurface(theme, flatModernSurface)) {
                return "none";
            }
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return modernChoiceBorder(theme, ChoiceTokens.resolveChoiceBorderColorNormal(theme));
            }
            return `0 0 0 ${theme.ChoiceListBackgroundBorderWidth}px ${theme.ControlBorderColor} inset`;
        },
        borderRadius: ({ theme }: { theme: Theme }) => ChoiceTokens.resolveChoiceBorderRadius(theme),
        boxSizing: "border-box",
        cursor: "pointer",
        display: ({ hasOpenEnd, openEndSize }: any) =>
            hasOpenEnd && openEndSize === "Full" ? "block" : "flex",
        flexWrap: "wrap",
        margin: ({
            theme,
            horizontal,
            flatModernSurface,
        }: {
            theme: Theme;
            horizontal?: boolean;
            flatModernSurface?: boolean;
        }) => (isFlatModernSurface(theme, flatModernSurface) ? 0 : getButtonChoiceMargin(theme, horizontal)),
        color: ({ theme }: { theme: Theme }) =>
            ChoiceTokens.useModernChoiceStyles(theme)
                ? ChoiceTokens.resolveModernChoiceLabelColor(theme)
                : foregroundColor(theme, controlBackgroundColor(theme)),
        overflow: ({ theme }: { theme: Theme }) =>
            ChoiceTokens.useModernChoiceStyles(theme) ? "visible" : "hidden",
        padding: ({ theme }: { theme: Theme }) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return ChoiceTokens.resolveChoicePaddingModern(theme);
            }
            return backgroundless(theme) ? [[8, 13]] : [[8, 26]];
        },
        [`@media (max-width: ${MOBILE_WIDTH}px)`]: {
            padding: ({
                theme,
                isCarrouselScaleHorizontalRow,
            }: {
                theme: Theme;
                isCarrouselScaleHorizontalRow?: boolean;
            }) => {
                if (isCarrouselScaleHorizontalRow === true) {
                    return [[8, 6]];
                }
                if (ChoiceTokens.useModernChoiceStyles(theme)) {
                    return ChoiceTokens.resolveChoicePaddingModern(theme);
                }
                return backgroundless(theme) ? [[8, 13]] : [[8, 26]];
            },
        },
        textAlign: ({ theme }: { theme: Theme }) =>
            theme.DisplayChoiceIcon === "right" ? "right" : "",
        transition: "background 200ms, box-shadow 200ms, outline-color 200ms",
        userSelect: "none",
        outline: "2px solid transparent",
        "& .check-box__icon": {
            display: ({ theme }: { theme: Theme }) => (!theme.ShowCheckboxSettings ? "none" : ""),
        },
        "& .radio__icon": {
            display: ({ theme }: { theme: Theme }) => (!theme.ShowRadioSettings ? "none" : ""),
        },
        "& .spacer": {
            display: ({ theme, type }: { theme: Theme; type: "checkbox" | "radio" }) =>
                (type === "checkbox" && !theme.ShowCheckboxSettings) ||
                (type === "radio" && !theme.ShowRadioSettings)
                    ? "none"
                    : "",
        },
    },
    choiceHeader: {
        composes: "choice__header",
        fontWeight: 700,
        margin: ({ theme }: { theme: Theme }) => [
            [getSpacing(theme, theme.ChoiceSpacing, theme.QuestionSpacing?.WithinComponents), 0],
        ],
    },
    choiceText: {
        composes: "choice__text",
        fontWeight: ({ theme, fontWeight }: StyleProps & { fontWeight?: number }) => {
            if (fontWeight !== undefined) {
                return fontWeight;
            }
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return ChoiceTokens.resolveChoiceLabelFontWeight(theme);
            }
            return 700;
        },
    },
    choiceContainer: {
        composes: "choice__container",
        alignItems: "center",
        display: "flex",
        flexGrow: 1,
        flexDirection: ({ theme, type }: { theme: Theme; type: "checkbox" | "radio" }) =>
            (type === "checkbox" && theme.CheckBoxIconAlignment === "right") ||
            (type === "radio" && theme.RadioIconAlignment === "right")
                ? "row-reverse"
                : "row",
        margin: ({ theme }: { theme: Theme }) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return theme.ChoiceContainerMarginModern ?? [[0, 0]];
            }
            return [[8, 0]];
        },
    },
    choiceDisabled: {
        composes: "choice--disabled",
        opacity: 0.5,
        pointerEvents: "none",
    },
    choiceSelected: {
        background: ({ theme }: { theme: Theme }) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return alpha(theme.ControlBackground, theme.ChoiceListBackgroundAlpha);
            }
            return !backgroundless(theme) && theme.PrimaryColor;
        },
        boxShadow: ({ theme, flatModernSurface }: StyleProps) => {
            if (isFlatModernSurface(theme, flatModernSurface)) {
                return "none";
            }
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return modernChoiceActiveBorder(theme, theme.PrimaryColor);
            }
            return `0 0 0 ${theme.ChoiceListBackgroundBorderWidth}px ${borderColor(
                theme,
                theme.PrimaryColor,
            )} inset`;
        },
        color: ({ theme }: { theme: Theme }) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return ChoiceTokens.resolveModernChoiceLabelColor(theme);
            }
            return backgroundless(theme)
                ? theme.PrimaryColor
                : foregroundColor(theme, theme.PrimaryColor);
        },
        composes: "choice--selected",
    },
    choiceHover: {
        composes: "choice--hover",
        background: ({ theme, selected }: StyleProps) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return selected
                    ? ChoiceTokens.resolveChoiceBackgroundSelectedHover(theme)
                    : ChoiceTokens.resolveChoiceBackgroundHover(theme);
            }
            if (backgroundless(theme)) {
                return undefined;
            }
            return selected
                ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                : darken(theme.ControlBackground, theme.ActiveBackgroundValue);
        },
        color: ({ theme, selected }: StyleProps) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return ChoiceTokens.resolveModernChoiceLabelColor(theme);
            }
            return selected
                ? foregroundColor(theme, darken(theme.PrimaryColor, theme.ActiveHighlightValue))
                : foregroundColor(
                      theme,
                      darken(theme.ControlBackground, theme.ActiveBackgroundValue),
                  );
        },
        boxShadow: ({ theme, selected, flatModernSurface }: StyleProps) => {
            if (isFlatModernSurface(theme, flatModernSurface)) {
                return "none";
            }
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                const border = selected
                    ? theme.PrimaryColor
                    : ChoiceTokens.resolveChoiceBorderColorHover(theme);
                return selected
                    ? modernChoiceActiveBorder(theme, border)
                    : modernChoiceBorder(theme, border);
            }
            return `0 0 0 ${theme.ChoiceListBackgroundBorderWidth}px ${borderColor(
                theme,
                selected
                    ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                    : darken(theme.ControlBackground, theme.ActiveHighlightValue),
            )} inset`;
        },
    },
    /** Modern keyboard focus ring — toggled by {@link ButtonChoice} when focus is not pointer-initiated. */
    choiceKeyboardFocus: {
        composes: "choice--keyboard-focus",
        ...modernChoiceKeyboardFocusStyles,
    },
    choiceActive: {
        composes: "choice--active",
        background: ({ theme, selected }: StyleProps) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return undefined;
            }
            if (backgroundless(theme)) {
                return undefined;
            }
            return selected
                ? darken(theme.PrimaryColor, theme.ActiveHighlightValue)
                : darken(theme.ControlBackground, theme.ActiveBackgroundValue);
        },
        color: ({ theme, selected }: StyleProps) => {
            if (ChoiceTokens.useModernChoiceStyles(theme)) {
                return undefined;
            }
            return selected
                ? foregroundColor(theme, darken(theme.PrimaryColor, theme.ActiveHighlightValue))
                : foregroundColor(
                      theme,
                      darken(theme.ControlBackground, theme.ActiveBackgroundValue),
                  );
        },
        outlineColor: ({ theme }: { theme: Theme }) =>
            ChoiceTokens.useModernChoiceStyles(theme) ? undefined : backgroundless(theme) && theme.PrimaryColor,
    },
    spacer: {
        composes: "spacer",
        width: ({ theme }: { theme: Theme }) =>
            ChoiceTokens.useModernChoiceStyles(theme)
                ? ChoiceTokens.resolveChoiceIconGapModern(theme)
                : 20,
        flex: 1,
    },
});

